import { apiClient } from "../../lib/apiClient";
import { ProjectListItem } from "../auth/useAuth";

export interface CreateProjectInput {
  name: string;
  icon_base64?: string;
}

/**
 * The project as POST /projects returns it — the detail shape, not the list
 * shape: it carries the icon blob and members_count, and none of the
 * list-only aggregates (has_icon, players_count).
 */
export interface CreatedProject {
  id: string;
  name: string;
  icon_base64?: string | null;
  etag: string;
  status: "active" | "suspended";
  created_at: string;
  last_activity_at: string | null;
  members_count: number;
}

export interface CreateProjectResponse {
  project: CreatedProject;
  api_key_plaintext: string;
}

/** Stored project lifecycle status. "setup" is a derived UI-only state. */
export type ProjectStatus = "active" | "suspended";

export interface ProjectDetail {
  id: string;
  name: string;
  icon_base64?: string | null;
  etag: string;
  /** Stored lifecycle status — "active" or "suspended". */
  status: ProjectStatus;
  created_at: string;
  last_activity_at: string | null;
  members_count: number;
  /** Raw project config JSON object. May be absent on legacy responses. */
  config?: unknown;
  /** The admin who created the project — null if unset; email null if deleted. */
  created_by: { id: string; email: string | null } | null;
  /** Number of outstanding (not-yet-accepted) member invites. */
  pending_invites_count: number;
  /**
   * Whether THIS SERVER has a bucket for cloud saves (`SH_GCS_BUCKET`). A
   * fact about the deployment carried on the project because the config
   * page and the player card both need it beside the project's own switch.
   */
  cloud_save_storage_configured?: boolean;
}

export interface PatchProjectInput {
  name?: string;
  icon_base64?: string | null;
  config?: unknown;
}

export async function listProjects(): Promise<ProjectListItem[]> {
  const r = await apiClient.get<{ projects: ProjectListItem[] }>(
    "/api/admin/projects",
  );
  return r.data.projects;
}

export async function createProject(
  input: CreateProjectInput,
): Promise<CreateProjectResponse> {
  const r = await apiClient.post<CreateProjectResponse>(
    "/api/admin/projects",
    input,
  );
  return r.data;
}

export async function getProject(
  id: string,
): Promise<{ data: ProjectDetail; etag: string | null }> {
  return apiClient.get<ProjectDetail>(
    `/api/admin/projects/${encodeURIComponent(id)}`,
  );
}

/**
 * What a PATCH echoes back: the project's own row, WITHOUT the two fields the
 * detail GET adds by asking further questions of the database — `created_by`
 * and `pending_invites_count` (see `projectDetailDTO` vs `projectDTO` in
 * internal/admin/api/handler/projects.go).
 *
 * Named apart from `ProjectDetail` because the difference decides how a save
 * updates the cache: the answer is laid OVER the record already there, never
 * put in its place, or the overview page would lose the creator's email and
 * the pending-invite count to a config save on another screen.
 */
export type PatchedProject = Omit<
  ProjectDetail,
  "created_by" | "pending_invites_count"
>;

export async function patchProject(
  id: string,
  input: PatchProjectInput,
  etag: string,
): Promise<{ data: PatchedProject; etag: string | null }> {
  return apiClient.patch<PatchedProject>(
    `/api/admin/projects/${encodeURIComponent(id)}`,
    input,
    etag,
  );
}

/** Suspend a project — super_admin only. Returns the updated project. */
export async function suspendProject(id: string): Promise<ProjectDetail> {
  const r = await apiClient.post<ProjectDetail>(
    `/api/admin/projects/${encodeURIComponent(id)}/suspend`,
  );
  return r.data;
}

/** Unsuspend (reactivate) a project — super_admin only. Returns the updated project. */
export async function unsuspendProject(id: string): Promise<ProjectDetail> {
  const r = await apiClient.post<ProjectDetail>(
    `/api/admin/projects/${encodeURIComponent(id)}/unsuspend`,
  );
  return r.data;
}

/**
 * Permanently delete a project — super_admin only. The backend requires an
 * If-Match etag and cascades every child row (players, guilds, chat, help
 * requests, memberships, invites).
 */
export async function deleteProject(id: string, etag: string): Promise<void> {
  await apiClient.delete<void>(
    `/api/admin/projects/${encodeURIComponent(id)}`,
    etag,
  );
}

// === Project data wipe ================================================= //
// The one destructive operation that empties a project of its players, guilds
// and bots and leaves every setting standing. See §6 of
// docs/superpowers/plans/2026-08-13-project-data-purge.md for the contract.

/** How far one step of the wipe got. Mirrors the `state` in `progress`. */
export type WipeStepState = "todo" | "running" | "done" | "failed";

/**
 * One line of the task's progress list — the seven the dialog draws: the
 * freeze, the five groups of the deletion plan, and the finish.
 */
export interface ProjectWipeStep {
  key: string;
  label: string;
  state: WipeStepState;
  /** Rows this step has removed so far. 0 for the freeze and the finish. */
  rows: number;
}

/**
 * A wipe task as the server keeps it. It outlives the dialog that started it —
 * closing the window does not stop the deletion — so every screen that wants
 * to know what is happening reads this, and none of them keeps its own idea of
 * it.
 */
export interface ProjectWipeTask {
  id: string;
  status: "running" | "done" | "failed";
  started_at: string;
  /** null while it runs. */
  finished_at: string | null;
  /**
   * The admin who started it. Null on two counts, and the wire tells them
   * apart in no way at all: the account has since been deleted — the task row
   * outlives it (ON DELETE SET NULL) — or the caller is a plain project
   * member, whose copy the handler blanks because the email can belong to a
   * super_admin who is not on this project's team.
   *
   * Nullable and not optional, because `toWipeTaskDTO` tags the field without
   * `omitempty` and a member receives `"started_by": null` — one shape for one
   * endpoint, as the handler's own comment puts it. The single reader here —
   * the banner's "Started … by …" — drops its clause on null and has no second
   * question to ask.
   */
  started_by: { id: string; email: string | null } | null;
  rows_deleted: number;
  /** Completed steps over seven. */
  percent: number;
  steps: ProjectWipeStep[];
  /**
   * The failure's message when `status` is "failed", and null otherwise — also
   * null in a plain member's copy, because the raw text is a Postgres error
   * naming the schema's own constraints.
   *
   * Nothing in the panel prints it: every state the two wipe screens draw is
   * decided by `status`, `percent` and `steps`, all of which every member
   * gets. It is declared because it is on the wire, and a reader of this type
   * should not have to open the Go handler to learn that.
   */
  error: string | null;
}

/** Both wipe endpoints answer in the same envelope; only the GET may be null. */
interface WipeTaskResponse {
  task: ProjectWipeTask | null;
}

/**
 * Everything the confirmation dialog's two sections print, and nothing else.
 * The last two fields are the "What stays" numbers.
 */
export interface ProjectWipePreflight {
  players: number;
  bots: number;
  guilds: number;
  chat_messages: number;
  help_requests: number;
  help_donations: number;
  gifts: number;
  friendships: number;
  friend_requests: number;
  leaderboard_cycles: number;
  leaderboard_scores: number;
  leaderboard_reward_grants: number;
  leaderboards: number;
  bot_phrases: number;
}

/**
 * Start emptying a project — super_admin only. Answers 202 with the task that
 * is now running.
 *
 * A second wipe of the same project fails with an `ApiError` carrying
 * `wipe_in_progress` (409). That is not a race this client can lose quietly:
 * the refusal comes from a partial unique index, so it is the database saying
 * one is already running, and the honest answer is to show that one.
 */
export async function startProjectWipe(id: string): Promise<ProjectWipeTask> {
  const r = await apiClient.post<WipeTaskResponse>(
    `/api/admin/projects/${encodeURIComponent(id)}/wipe`,
  );
  return r.data.task as ProjectWipeTask;
}

/**
 * The project's latest wipe task, or null when it has never been wiped.
 *
 * The envelope is unwrapped here rather than at the call sites: "no task" is
 * `null`, which is a value react-query caches, and every reader then asks the
 * one question that matters — is something running — instead of unwrapping
 * first.
 */
export async function getProjectWipe(
  id: string,
): Promise<ProjectWipeTask | null> {
  const r = await apiClient.get<WipeTaskResponse>(
    `/api/admin/projects/${encodeURIComponent(id)}/wipe`,
  );
  return r.data.task;
}

/** The counts the confirmation dialog shows before anything is deleted. */
export async function getProjectWipePreflight(
  id: string,
): Promise<ProjectWipePreflight> {
  const r = await apiClient.get<ProjectWipePreflight>(
    `/api/admin/projects/${encodeURIComponent(id)}/wipe/preflight`,
  );
  return r.data;
}

/** One metric's aggregated 7-day view. Mirrors store.MetricSeries. */
export interface MetricSeries {
  /** All-time count. null for chat — not computed server-side. */
  total: number | null;
  last_7d: number;
  /** 7 daily counts, oldest day first, today last. */
  series: number[];
}

/** Game-level analytics payload. Mirrors store.ProjectAnalytics. */
export interface ProjectAnalytics {
  window_days: number;
  generated_at: string;
  players: MetricSeries;
  guilds: MetricSeries;
  chat: MetricSeries;
}

/**
 * Fetch read-only game-level analytics for a project. Backed by a lazy,
 * Redis-cached endpoint — see docs/superpowers/specs/2026-05-21-admin-analytics-design.md.
 */
export async function getProjectAnalytics(
  id: string,
): Promise<ProjectAnalytics> {
  const r = await apiClient.get<ProjectAnalytics>(
    `/api/admin/projects/${encodeURIComponent(id)}/analytics`,
  );
  return r.data;
}

/**
 * How many players are connected right now, and how many of those are the
 * simulation. Mirrors the handler's online DTO.
 *
 * The breakdown is not decoration: on a project running the cold-start
 * simulation, "412 online" without the second figure says nothing about how
 * many people are actually playing.
 */
export interface ProjectOnline {
  /** Everyone with live presence — `real + bots`. */
  total: number;
  /** Living players among them. */
  real: number;
  /** The simulation's players among them. */
  bots: number;
  /** When the server took the count, by the server's clock. */
  generated_at: string;
}

/**
 * Read the project's presence count.
 *
 * A request of its own rather than a field on `/analytics`: that one is
 * computed over a 7-day window and cached for ten minutes, and a "who is
 * online" that lags by ten minutes is worse than none. This endpoint is cached
 * for seconds.
 */
export async function getProjectOnline(id: string): Promise<ProjectOnline> {
  const r = await apiClient.get<ProjectOnline>(
    `/api/admin/projects/${encodeURIComponent(id)}/online`,
  );
  return r.data;
}

/** The period every number in the Request signing section is stated over. */
export const SIGNING_WINDOW_DAYS = 7;

/**
 * What the project's `request_signing` blob actually parses to on the server.
 *
 * The three flags are the whole reason this travels: the form shows what is
 * WRITTEN in the blob, and these say what is IN FORCE where the two differ. An
 * operator only reaches such a state through Raw JSON or the platform API, and
 * nothing else would ever tell them they had missed.
 */
export interface SigningConfigState {
  /** The mode in force — `off` whenever the blob names one that is not a mode. */
  mode: string;
  /** The window in force, in seconds. */
  clock_skew_sec: number;
  /** The blob names a mode that does not exist, so `off` is in force. */
  mode_unknown: boolean;
  /** The blob asks for a window above the ceiling, so the ceiling is in force. */
  clock_skew_clamped: boolean;
  /** The blob's window is not a whole number, so the default is in force. */
  clock_skew_invalid: boolean;
  /**
   * The WHOLE blob would not parse, which is a state rather than a fourth flag:
   * the parser never reaches the section. The game API does not read such a
   * project as `off` — it lets every request through unchecked — so the form
   * must not show one either.
   */
  config_unreadable: boolean;
}

/**
 * The rollout counters of the Request signing section.
 *
 * `available` is a field rather than an HTTP status because the two empty
 * states are different stories: a project that has never run `log` has nothing
 * to show and is perfectly healthy, while a counter store that did not answer
 * is a breakage. Neither one fails the page, and neither one stops the setting
 * below from saving.
 */
export interface SigningStats {
  window_days: number;
  available: boolean;
  total: number;
  /** Count per result label, zero-count results absent. */
  by_result: Record<string, number>;
  config: SigningConfigState;
}

/**
 * Fetch the signing counters and the parsed state of the signing config.
 * Uncached on both ends: the Config page is not opened often enough to pay for
 * invalidating one, and the numbers do not move while it is open.
 */
export async function getSigningStats(id: string): Promise<SigningStats> {
  const r = await apiClient.get<SigningStats>(
    // The window is asked for rather than left to the server's default, so the
    // page states the period it is showing instead of inheriting one. Seven
    // days is what the player counter's TTL keeps, which is what makes the two
    // halves of this feature talk about the same week.
    `/api/admin/projects/${encodeURIComponent(id)}/signing-stats?days=${SIGNING_WINDOW_DAYS}`,
  );
  return r.data;
}

/**
 * The Invites section's counters: how the referral funnel actually behaves,
 * which is what the trigger and the cap are chosen against.
 *
 * Three numbers rather than a rate, because they are three different things to
 * do something about — codes accepted, pairs paid, and the invitees still short
 * of the trigger. `window_days` is the server's, not asked for: unlike the
 * signing counters there is no second half of the feature to keep in step with,
 * so the page states the period it was given.
 */
export interface ReferralStats {
  window_days: number;
  accepted: number;
  rewarded: number;
  waiting: number;
}

/** Fetch the referral counters. Uncached on both ends, as the signing ones are:
 *  the numbers do not move while the Config page is open. */
export async function getReferralStats(id: string): Promise<ReferralStats> {
  const r = await apiClient.get<ReferralStats>(
    `/api/admin/projects/${encodeURIComponent(id)}/referral-stats`,
  );
  return r.data;
}

/**
 * One revision's row of the Client versions card.
 *
 * Mirrors handler.contractRevisionView. Every revision this server knows is a
 * row even at zero players — hiding the empty one would make «nobody is on it»
 * and «we do not count it» indistinguishable, and telling those apart is the
 * card's whole job.
 */
export interface ContractRevision {
  /** The revision as the aggregate keys it: a decimal, or `"future"`. */
  label: string;
  /** Where it sits relative to the running build. */
  standing: "behind" | "current" | "ahead";
  /** Distinct players seen on it over the window. */
  players: number;
  /** SDK versions seen on it, most-seen first. May be empty: a caller that
   *  sends no version header — curl, a game backend on the S2S routes — is
   *  counted as a player and contributes no build. */
  sdk_versions: string[];
  /** How many registry entries a client at this revision is served through. */
  shims: number;
}

/** One entry of the compatibility registry. Mirrors handler.contractShimView. */
export interface ContractShim {
  name: string;
  /** The revision that introduced the change; the shim serves everything below it. */
  revision: number;
  /**
   * **This project's** players on the revisions this shim serves — and `null`,
   * never 0, when the aggregate did not answer.
   *
   * It decides nothing. It is on screen because «is anybody in my game still on
   * an old build» is the question a studio actually has, and because a reader
   * who sees only the platform figure cannot tell whether the entry is kept for
   * them or for somebody else.
   */
  players: number | null;
  /**
   * The same count across **every** project, and the one the verdict is taken
   * from: the registry is one table applied to every game, so a shim goes only
   * when nobody anywhere has needed it. `null` when the platform census did not
   * answer.
   */
  platform_players: number | null;
  /** The share of players under which this shim stops paying for itself, as its
   *  own entry declares it. 0 on a form shim, which costs nothing. Compared
   *  against the PLATFORM share — a threshold standing on a different number
   *  than the verdict is worse than no threshold. */
  retire_below: number;
}

/**
 * The platform-wide half of the answer: the population a platform-wide registry
 * has to be judged against.
 *
 * A project's own numbers stay on screen beside these and decide nothing — a
 * demo or a staging project is honestly empty, its window honestly covered, and
 * a verdict read off it would retire an entry other studios' players are still
 * being served through.
 */
export interface ContractPlatform {
  /** Whether the platform census answered. Separate from the project's: either
   *  may fail on its own. */
  available: boolean;
  /** Whether the platform census has been watching for the whole window. Also
   *  separate: a project mark reading «covered» over an emptied platform census
   *  would reopen the same hole one floor up. */
  window_covered: boolean;
  /** The day the platform census began (`YYYY-MM-DD`), or `""`. */
  observed_since: string;
  /** Everybody the platform saw over the window — the denominator each shim's
   *  share is stated against. */
  players: number;
}

/**
 * What both client-contract cards read. One answer and not two: a shim's share
 * is a fraction of the very population the revision rows add up to, and two
 * routes could be read a minute apart and disagree about the denominator.
 */
export interface ContractStats {
  /** The window every number here is counted over — the aggregate's own thirty
   *  days, not the page's seven, which is why the card draws its own chip. */
  window_days: number;
  /** Whether the aggregate answered at all. False is «nobody could be asked»,
   *  which must never be rendered as a verdict. */
  available: boolean;
  /**
   * Whether the aggregate has been watching this project for at least the
   * whole window, and is therefore entitled to a zero.
   *
   * The second guard on the verdict, and the one `available` cannot give: a
   * Redis that lost its data ANSWERS — zero, on every revision — and that
   * reading retires every shim there is. A project on its first day produces
   * the same false zero for an honest reason. Both are «we do not know yet».
   */
  window_covered: boolean;
  /** The UTC day the aggregate first counted this project (`YYYY-MM-DD`), or
   *  `""` when it holds no mark. Said out loud whenever `window_covered` is
   *  false — «zero since Tuesday» is not «zero for a month». */
  observed_since: string;
  revisions: ContractRevision[];
  /** The same census with the project cut removed — what every verdict below is
   *  taken from. */
  platform: ContractPlatform;
  /** The registry as it is written, oldest entry first. Present even when
   *  `available` is false: the table is in the server binary. */
  shims: ContractShim[];
}

/**
 * Fetch the revision distribution and the registry, for Overview's two
 * client-contract cards.
 *
 * No window parameter, unlike the signing route: the thirty days are the one
 * decision this card exists to protect, and a page that asked for its own
 * number could quietly ask for a narrower one.
 */
export async function getContractStats(id: string): Promise<ContractStats> {
  const r = await apiClient.get<ContractStats>(
    `/api/admin/projects/${encodeURIComponent(id)}/contract-stats`,
  );
  return r.data;
}
